import * as z from 'zod';
import { loadRemote } from '@module-federation/runtime';
import { AppSchema, AuthSchema, NavItemsSchema, PendoSchema, RemotesSchema, SidebarSchema } from '@platform-ui/portal-config';

/**
 * Type for dynamic import functions used by module federation.
 */
export type ImportFunction = () => Promise<unknown>;

/**
 * Creates an import function that uses Module Federation's loadRemote()
 * to dynamically load modules at runtime.
 *
 * Remotes are registered by federation-plugin.ts from portal-config.json
 * before any modules are loaded - no hardcoded registry needed.
 */
function getImportFunction(modulePath: string | undefined): ImportFunction | undefined {
    if (!modulePath) return undefined;
    return () => loadRemote(modulePath) as Promise<unknown>;
}

/**
 * Runtime schema that extends the shared base schemas with browser-only transforms.
 * The transforms add `importRemote*` functions backed by Module Federation's loadRemote().
 */
const EnvSchema = z.object({
    auth: AuthSchema,
    pendo: PendoSchema.optional(),
    sidebar: SidebarSchema.transform(sidebar => ({
        ...sidebar,
        importRemoteSidebarMount: getImportFunction(sidebar.remoteSidebarMount)
    })),
    remotes: RemotesSchema,
    apps: z.array(
        AppSchema.transform(app => ({
            ...app,
            importRemotePageMount: getImportFunction(app.remotePageMount),
            importRemoteNavItems: getImportFunction(app.remoteNavItems),
            importRemoteUserMenuItems: getImportFunction(app.remoteUserMenuItems),
            importRemoteHeaderActionsMount: getImportFunction(app.remoteHeaderActionsMount)
        }))
    )
});

/**
 * Loads and validates the application's runtime configuration from `/portal-config.json`.
 *
 * ## Overview
 * - The file `/public/portal-config.json` is copied verbatim by Vite into the build output
 *   root (`dist/portal-config.json`).
 * - This function fetches that file at runtime using a path derived from
 *   `import.meta.env.BASE_URL`, which correctly handles non-root deployments
 *   (e.g., when the app is served from `/myapp/` instead of `/`).
 * - The JSON is parsed and validated against `EnvSchema` to ensure all expected
 *   keys and types are present.
 * - If any validation fails, a detailed, human-readable error is thrown that
 *   lists the invalid fields and reasons.
 *
 * ## Behavior
 * - Uses `await` at the module top level, so the app will not render or execute
 *   until configuration is successfully loaded and validated.
 * - In development, `cache: 'no-cache'` ensures the latest `portal-config.json` is always
 *   fetched without relying on browser caching.
 * - In production, the same code runs against the built `dist/portal-config.json`.
 *
 * ## Errors
 * - Throws if the network request fails (e.g., 404 or 500).
 * - Throws if validation fails, with a detailed field-by-field summary.
 *
 * ## Returns
 * A strongly-typed object matching `EnvSchema` that can be safely imported and
 * used anywhere in the app.
 */
async function createEnv() {
    // Construct an absolute URL to /env.json that works with any Vite base path.
    const envUrl = `${import.meta.env.BASE_URL}portal-config.json`;

    // Fetch the configuration file from the server.
    const res = await fetch(envUrl, { cache: 'no-cache' });
    if (!res.ok) {
        throw new Error(`Failed to load ${envUrl}: ${res.status} ${res.statusText}`);
    }

    // Parse and validate the JSON structure.
    const json = await res.json();

    const parsed = EnvSchema.safeParse(json);

    // If validation failed, build a readable error summary.
    if (!parsed.success) {
        const { fieldErrors } = z.flattenError(parsed.error);
        const details = Object.entries(fieldErrors)
            .map(([key, messages]) => `- ${key}: ${messages?.join(', ')}`)
            .join('\n');
        throw new Error(`Invalid env (portal-config.json) provided:\n${details}`);
    }

    // Return the validated configuration.
    return parsed.data;
}

/**
 * The fully-validated environment configuration, loaded once at module import.
 *
 * Because this uses a top-level await, any module that imports `env`
 * automatically waits for configuration to finish loading and validation before
 * executing. This guarantees `env` is always ready and type-safe.
 *
 * Example:
 * ```ts
 * import { env } from './env';
 * console.log(env.auth.apiUrl);
 * console.log(env.sidebar.remoteSidebarMount);
 * console.log(env.apps[0].path);
 * ```
 */
export const env = await createEnv();

/**
 * Type alias representing the structure of the validated environment.
 * Useful for generics or dependency injection.
 */
export type Env = typeof env;

/**
 * Type alias for a single app configuration (post-transform, includes importRemote* functions).
 */
export type AppConfig = Env['apps'][number];

/**
 * Type alias for authentication configuration.
 */
export type AuthConfig = Env['auth'];

/**
 * Type alias for Pendo configuration.
 */
export type PendoConfig = Env['pendo'];

/**
 * Type alias for sidebar configuration (post-transform, includes importRemoteSidebarMount).
 */
export type SidebarConfig = Env['sidebar'];

/**
 * Type alias for a single nav item.
 */
export type NavItem = z.infer<typeof NavItemsSchema>;

/**
 * Portal config when loading the production config file.
 * Used at build/deploy time. Same shape as Env; production file is selected by prebuild.
 */
export type PortalConfigProduction = Env;

/**
 * Portal config when loading the dev config file.
 * Used when serving locally. Same shape as Env; dev file is selected by predev.
 */
export type PortalConfigDev = Env;
